import React from 'react';
import { Mail, Phone } from 'lucide-react';

interface NewsletterEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const NewsletterEditor: React.FC<NewsletterEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Fırsatlardan Haberdar Olun'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Alt Başlık
            </label>
            <input
              type="text"
              name="subtitle"
              value={settings?.subtitle || 'En güncel banka kampanyaları ve fırsatlardan haberdar olun'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Buton Metni
            </label>
            <input
              type="text"
              name="buttonText"
              value={settings?.buttonText || 'Abone Ol'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başarı Başlığı
            </label>
            <input
              type="text"
              name="successTitle"
              value={settings?.successTitle || 'Teşekkürler!'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Başarı Mesajı
          </label>
          <textarea
            name="successMessage"
            value={settings?.successMessage || 'Başvurunuz başarıyla alındı. En güncel kampanya ve fırsatlardan haberdar olmak için e-posta ve SMS bildirimlerinizi takip edebilirsiniz.'}
            onChange={handleSettingChange}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>
      
      {/* Form Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Form Ayarları</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              E-posta Placeholder
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                name="emailPlaceholder"
                value={settings?.emailPlaceholder || 'E-posta Adresi'}
                onChange={handleSettingChange}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Telefon Placeholder
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                name="phonePlaceholder"
                value={settings?.phonePlaceholder || 'Telefon Numarası'}
                onChange={handleSettingChange}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            KVKK Metni
          </label>
          <textarea
            name="kvkkText"
            value={settings?.kvkkText || 'Kişisel verilerimin işlenmesine ilişkin Aydınlatma Metni\'ni okudum ve kişisel verilerimin bu kapsamda işlenmesini kabul ediyorum.'}
            onChange={handleSettingChange}
            rows={2}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Pazarlama Metni
          </label>
          <textarea
            name="marketingText"
            value={settings?.marketingText || 'KrediPark\'a özel kampanyalar, avantajlı teklifler ve güncel fırsatlardan e-posta ve SMS yoluyla haberdar olmak istiyorum.'}
            onChange={handleSettingChange}
            rows={2}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showPhone"
              checked={settings?.showPhone !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">Telefon Alanını Göster</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="showEmail"
              checked={settings?.showEmail !== false}
              onChange={handleSettingChange}
              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
            />
            <span className="text-sm text-slate-600">E-posta Alanını Göster</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default NewsletterEditor;